import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to update map center when center prop changes
function ChangeView({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center.lat && center.lng) {
      map.setView([center.lat, center.lng], zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
}

// Rectangle Drawer Component
function RectangleDrawer({ onRectangleComplete, isDrawing, onClear, polygonsRef }) {
  const map = useMap();
  const pointsRef = useRef([]);
  const markersRef = useRef([]);
  const linesRef = useRef([]);
  const currentPolygonRef = useRef(null);
  
  // Expose clear function to parent
  useEffect(() => {
    if (onClear) {
      onClear.current = () => {
        // Clear current drawing
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        linesRef.current.forEach(line => line.remove());
        linesRef.current = [];
        if (currentPolygonRef.current) {
          currentPolygonRef.current.remove();
          currentPolygonRef.current = null;
        }
        pointsRef.current = [];
        
        // Clear all polygons if polygonsRef is provided
        if (polygonsRef && polygonsRef.current) {
          polygonsRef.current.forEach(polygon => {
            if (polygon && polygon.remove) {
              polygon.remove();
            }
          });
          polygonsRef.current = [];
        }
      };
    }
  }, [onClear, polygonsRef]);

  useEffect(() => {
    const clearAll = (keepPolygon = false) => {
      // Remove all markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      // Remove all lines
      linesRef.current.forEach(line => line.remove());
      linesRef.current = [];

      // Remove current polygon only if not keeping it
      if (!keepPolygon && currentPolygonRef.current) {
        currentPolygonRef.current.remove();
        currentPolygonRef.current = null;
      }

      if (!keepPolygon) {
        pointsRef.current = [];
      }
    };

    if (!isDrawing) {
      // Don't clear if we have polygons - keep them all visible
      // All polygons in polygonsRef should remain visible even when isDrawing is false
      if ((currentPolygonRef.current) || (polygonsRef && polygonsRef.current && polygonsRef.current.length > 0)) {
        // Keep all polygons visible, just don't allow new clicks
        return;
      }
      // Only clear if there's no polygons at all
      clearAll();
      return;
    }

    // Reset points when starting to draw (but keep ALL existing polygons)
    pointsRef.current = [];
    // Only clear markers and lines, but keep all polygons in polygonsRef
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    linesRef.current.forEach(line => line.remove());
    linesRef.current = [];
    // Don't clear currentPolygonRef - it will be replaced when new polygon is created
    // But keep all polygons in polygonsRef visible
    
    console.log('Drawing started, isDrawing:', isDrawing);

    const handleMapClick = (e) => {
      // Only allow clicks if we have less than 4 points
      if (pointsRef.current.length >= 4) {
        return; // Already have 4 points, ignore clicks
      }

      const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
      pointsRef.current.push(newPoint);
      
      console.log(`Point ${pointsRef.current.length} added:`, newPoint);
      console.log('Total points:', pointsRef.current.length);

      // Add marker for the new point
      const marker = L.marker([newPoint.lat, newPoint.lng], {
        icon: L.icon({
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
        }),
      }).addTo(map);
      markersRef.current.push(marker);

      // Draw temporary red lines between points
      if (pointsRef.current.length > 1) {
        const prevPoint = pointsRef.current[pointsRef.current.length - 2];
        const line = L.polyline(
          [[prevPoint.lat, prevPoint.lng], [newPoint.lat, newPoint.lng]],
          { color: '#ff0000', weight: 2, dashArray: '5, 5' }
        ).addTo(map);
        linesRef.current.push(line);
      }

        // If 4 points are clicked, complete the rectangle
        if (pointsRef.current.length === 4) {
          console.log('Completing rectangle with 4 points:', pointsRef.current);
          
          // Remove all temporary lines first
          linesRef.current.forEach(line => line.remove());
          linesRef.current = [];

          // If there was a previous currentPolygonRef, make sure it's in the list
          if (currentPolygonRef.current && polygonsRef && polygonsRef.current) {
            // Check if it's already in the list
            if (!polygonsRef.current.includes(currentPolygonRef.current)) {
              polygonsRef.current.push(currentPolygonRef.current);
            }
          }

        // Create final red polygon with more visible styling
        const polygonCoords = pointsRef.current.map(p => [p.lat, p.lng]);
        console.log('Polygon coordinates:', polygonCoords);
        const polygon = L.polygon(polygonCoords, {
          color: '#ff0000',
          fillColor: '#ff0000',
          fillOpacity: 0.5, // Increased opacity for better visibility
          weight: 3, // Thicker border
          opacity: 0.8, // Border opacity
        }).addTo(map);
        
        // Add to polygons list BEFORE setting currentPolygonRef
        if (polygonsRef && polygonsRef.current) {
          polygonsRef.current.push(polygon);
          console.log('Total polygons on map:', polygonsRef.current.length);
        }
        
        // Set as current polygon
        currentPolygonRef.current = polygon;
        
        // Bring all polygons to front to ensure they're visible
        if (polygonsRef && polygonsRef.current) {
          polygonsRef.current.forEach(p => {
            if (p && p.bringToFront) {
              p.bringToFront();
            }
          });
        } else {
          polygon.bringToFront();
        }

        // Convert to format expected by parent - ensure we have exactly 4 points
        // Create a copy of the array to avoid reference issues
        const coordinates = pointsRef.current.slice().map(p => ({ lat: p.lat, lng: p.lng }));
        console.log('Sending coordinates to parent:', coordinates);
        console.log('Number of coordinates:', coordinates.length);
        console.log('pointsRef.current length:', pointsRef.current.length);
        console.log('pointsRef.current:', JSON.stringify(pointsRef.current));
        
        if (coordinates.length === 4) {
          onRectangleComplete(coordinates);
        } else {
          console.error('Error: Expected 4 points but got', coordinates.length);
          console.error('pointsRef.current contents:', pointsRef.current);
        }
      }
    };

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [map, isDrawing, onRectangleComplete]);


  return null;
}

// Search Component
function MapSearch({ onLocationSelect }) {
  const map = useMap();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const markerRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`,
        {
          headers: {
            'User-Agent': 'SolarEnergyApp/1.0',
          },
        }
      );
      const data = await response.json();
      setSearchResults(data);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSelectLocation = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Remove previous marker
    if (markerRef.current) {
      markerRef.current.remove();
    }

    // Add new marker
    markerRef.current = L.marker([lat, lng], {
      icon: L.icon({
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
      }),
    }).addTo(map);

    // Move map to location
    map.setView([lat, lng], map.getZoom());

    // Clear search
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);

    if (onLocationSelect) {
      onLocationSelect({ lat, lng });
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: '90%',
        maxWidth: '600px',
      }}
    >
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="ابحث عن موقع..."
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        />
        <button
          type="submit"
          style={{
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          بحث
        </button>
      </form>
      {showResults && searchResults.length > 0 && (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            marginTop: '5px',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {searchResults.map((result, index) => (
            <div
              key={index}
              onClick={() => handleSelectLocation(result)}
              style={{
                padding: '10px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
            >
              <div style={{ fontWeight: 'bold' }}>{result.display_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MapAreaSelector({ center, onAreaSelected, initialZoom = 20 }) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnCoordinates, setDrawnCoordinates] = useState(null);
  const clearRef = useRef(null);
  const polygonsRef = useRef([]); // Store all polygons

  const handleDrawClick = () => {
    setIsDrawing(true);
    setDrawnCoordinates(null);
    // Don't clear existing polygons, allow adding new ones
  };

  const handleRectangleComplete = (coordinates) => {
    setDrawnCoordinates(coordinates);
    setIsDrawing(false);
    if (onAreaSelected) {
      onAreaSelected(coordinates);
    }
  };

  const handleClear = () => {
    setIsDrawing(false);
    setDrawnCoordinates(null);
    // Call the clear function from RectangleDrawer
    if (clearRef.current) {
      clearRef.current();
    }
    // Clear all polygons
    polygonsRef.current = [];
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '600px' }}>
      <MapContainer
        center={center || [31.9522, 35.2332]} // Default to Jordan
        zoom={initialZoom}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
      >
        <TileLayer
          attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        <ChangeView center={center} zoom={initialZoom} />
        <MapSearch />
        <RectangleDrawer onRectangleComplete={handleRectangleComplete} isDrawing={isDrawing} onClear={clearRef} polygonsRef={polygonsRef} />
      </MapContainer>

      {/* Drawing buttons at the top near search */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '90%',
          maxWidth: '600px',
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          marginTop: '50px',
        }}
      >
        <button
          onClick={handleDrawClick}
          disabled={isDrawing}
          style={{
            padding: '10px 20px',
            backgroundColor: isDrawing ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isDrawing ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          رسم (4 نقاط)
        </button>
        {drawnCoordinates && (
          <button
            onClick={handleClear}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            مسح
          </button>
        )}
      </div>
    </div>
  );
}

export default MapAreaSelector;

